import {
  handleWithErrorMessage,
  handleWithMessageAndLoading,
  addGlobalMessage,
  errorHandler
} from "@/services/utils";
import api from "@/services/modules/users/users";
import {
  SET_IS_ADDING_USER,
  SET_IS_EDITING_USER_DETAILS,
  SET_IS_FETCHING_USER_ROLES,
  SET_IS_FETCHING_USER_SETTINGS,
  SET_USER_SETTINGS,
  SET_IS_FETCHING_USER_LOCATION_GROUPINGS,
  SET_USER_LOCATION_GROUPINGS,
  SET_IS_FETCHING_USER_EXPERIENCE_FUNCTIONS,
  SET_USER_EXPERIENCE_FUNCTIONS,
  SET_IS_FETCHING_BOOKING_MANAGERS,
  SET_BOOKING_MANAGERS,
  SET_IS_FETCHING_USER_DIVERSITY_TRAITS,
  SET_USER_DIVERSITY_TRAITS,
  SET_IS_FETCHING_USERS,
  SET_USERS
} from "@/store/modules/users/mutations";
import { SET_ORGANISATION_USERS } from "@/store/modules/settings/mutations";
import { GET_ORGANISATION_USERS } from "@/store/modules/settings/getters";
import { fromSettings } from "@/store/modules/settings";
import { fromAuth } from "@/store/modules/auth";
import { SET_USER_DATA } from "@/store/modules/auth/mutation-types";
import { GET_USER_DATA } from "@/store/modules/auth/getters";
import { size, first, map } from "lodash";

export const FETCH_USER_SETTINGS = "FETCH_USER_SETTINGS";
export const EDIT_USER_SETTINGS = "EDIT_USER_SETTINGS";
export const CREATE_USER = "CREATE_USER";
export const EDIT_USER_STAFF_DETAILS = "EDIT_USER_STAFF_DETAILS";
export const EDIT_USER = "EDIT_USER";
export const FETCH_USER_ROLES = "FETCH_USER_ROLES";
export const FETCH_USERS = "FETCH_USERS";
export const CHECK_EXISTING_EMAIL = "CHECK_EXISTING_EMAIL";
export const ADD_USER_JOB_PREFERENCE = "ADD_USER_JOB_PREFERENCE";
export const ADD_USER_DIVERSITY_TRAITS = "ADD_USER_DIVERSITY_TRAITS";
export const FETCH_USER_EXPERIENCE_FUNCTIONS =
  "FETCH_USER_EXPERIENCE_FUNCTIONS";
export const FETCH_USER_LOCATION_GROUPINGS = "FETCH_USER_LOCATION_GROUPINGS";
export const FETCH_BOOKING_MANAGERS = "FETCH_BOOKING_MANAGERS";
export const FETCH_USER_DIVERSITY_TRAITS = "FETCH_USER_DIVERSITY_TRAITS";
export const FETCH_CURRENT_USER = "FETCH_CURRENT_USER";
export const INVITE_USER = "INVITE_USER";
export const CREATE_WORKER_ACCOUNT = "CREATE_WORKER_ACCOUNT";

const usersActions = {
  async [FETCH_BOOKING_MANAGERS]({ dispatch, commit }, organisationId) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getBookingManagers(organisationId),
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_BOOKING_MANAGERS, isLoading),
      setDataFunction: ({ data }) => commit(SET_BOOKING_MANAGERS, data)
    });
  },
  async [FETCH_USER_SETTINGS]({ dispatch, commit }) {
    return handleWithErrorMessage({
      dispatch,
      request: api(commit).getUserSettings,
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_USER_SETTINGS, isLoading),
      setDataFunction: ({ data }) => commit(SET_USER_SETTINGS, data)
    });
  },
  async [EDIT_USER_SETTINGS]({ dispatch, commit }, editedSettings) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).editUserSettings(editedSettings),
      setDataFunction: ({ data }) => {
        commit(SET_USER_SETTINGS, data.attributes);
        addGlobalMessage(dispatch, {
          type: "success",
          meta: { title: "Successfully updated" }
        });
      }
    });
  },
  async [CREATE_USER]({ dispatch, commit }, userData) {
    return handleWithMessageAndLoading({
      dispatch,
      setLoadingFunction: isLoading => commit(SET_IS_ADDING_USER, isLoading),
      request: () => api(commit).createUser(userData),
      timeout: 15000
    });
  },
  async [CREATE_WORKER_ACCOUNT]({ dispatch, commit }) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).createWorkerAccount()
    });
  },
  async [CREATE_WORKER_ACCOUNT]({ dispatch, commit }) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).createWorkerAccount()
    });
  },
  async [EDIT_USER_STAFF_DETAILS]({ dispatch, commit, rootGetters }, userData) {
    const organisationUsers = rootGetters[fromSettings(GET_ORGANISATION_USERS)];
    return handleWithMessageAndLoading({
      dispatch,
      setLoadingFunction: isLoading =>
        commit(SET_IS_EDITING_USER_DETAILS, isLoading),
      request: () => api(commit).editUserStaffDetails(userData),
      setDataFunction: data => {
        const updatedUsers = [...organisationUsers].map(user => ({
          ...user,
          ...[data].find(item => item && item.id === user.id)
        }));

        commit(fromSettings(SET_ORGANISATION_USERS), updatedUsers, {
          root: true
        });
      }
    });
  },
  async [EDIT_USER]({ dispatch, commit, rootGetters }, { id, data }) {
    const userData = rootGetters[fromAuth(GET_USER_DATA)];

    return handleWithErrorMessage({
      dispatch,
      setDataFunction: ({ data }) => {
        commit(
          fromAuth(SET_USER_DATA),
          { ...userData, ...data },
          {
            root: true
          }
        );
      },
      setLoadingFunction: isLoading =>
        commit(SET_IS_EDITING_USER_DETAILS, isLoading),
      request: () => api(commit).editUser(id, data)
    });
  },
  async [FETCH_CURRENT_USER]({ dispatch, commit, rootGetters }) {
    const userData = rootGetters[fromAuth(GET_USER_DATA)];

    return handleWithErrorMessage({
      dispatch,
      setDataFunction: ({ data }) => {
        commit(
          fromAuth(SET_USER_DATA),
          { ...userData, ...data },
          {
            root: true
          }
        );
      },
      setLoadingFunction: isLoading =>
        commit(SET_IS_EDITING_USER_DETAILS, isLoading),
      request: () => api(commit).getUser(userData.id)
    });
  },
  async [FETCH_USER_ROLES]({ dispatch, commit }, id) {
    return handleWithErrorMessage({
      dispatch,
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_USER_ROLES, isLoading),
      request: () => api(commit).getUserRoles(id)
    });
  },
  async [CHECK_EXISTING_EMAIL]({ dispatch, commit }, email) {
    try {
      const filter = { email };
      const { data } = await api(commit).getUsers({ filter });
      const isExistingEmail = Boolean(size(data));
      return { user: first(data), isExistingEmail };
    } catch (exception) {
      errorHandler(dispatch, exception);
    }
  },
  async [FETCH_USER_EXPERIENCE_FUNCTIONS]({ dispatch, commit }, userId) {
    return handleWithErrorMessage({
      dispatch,
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_USER_EXPERIENCE_FUNCTIONS, isLoading),
      request: () => api(commit).getUserExperienceFunctions(userId),
      setDataFunction: ({ data }) => commit(SET_USER_EXPERIENCE_FUNCTIONS, data)
    });
  },
  async [FETCH_USER_LOCATION_GROUPINGS]({ dispatch, commit }, userId) {
    return handleWithErrorMessage({
      dispatch,
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_USER_LOCATION_GROUPINGS, isLoading),
      request: () => api(commit).getUserLocationGroupings(userId),
      setDataFunction: ({ data }) => commit(SET_USER_LOCATION_GROUPINGS, data)
    });
  },
  async [FETCH_USER_DIVERSITY_TRAITS]({ dispatch, commit }, userId) {
    return handleWithErrorMessage({
      dispatch,
      setLoadingFunction: isLoading =>
        commit(SET_IS_FETCHING_USER_DIVERSITY_TRAITS, isLoading),
      request: () => api(commit).getUserDiversityTraits(userId),
      setDataFunction: ({ data }) => {
        if (data) {
          commit(SET_USER_DIVERSITY_TRAITS, data);
        }
      }
    });
  },
  async [ADD_USER_JOB_PREFERENCE]({ dispatch, commit }, { userId, data }) {
    return handleWithErrorMessage({
      request: () => api(commit).addUserPreference(userId, data),
      dispatch
    });
  },
  async [ADD_USER_DIVERSITY_TRAITS]({ dispatch, commit }, { id, data }) {
    return handleWithErrorMessage({
      request: () => api(commit).addUserDiversityTraits(id, data),
      setLoadingFunction: isLoading =>
        commit(SET_IS_EDITING_USER_DETAILS, isLoading),
      dispatch
    });
  },
  async [INVITE_USER]({ dispatch, commit, rootGetters }, id) {
    const organisationUsers = rootGetters[fromSettings(GET_ORGANISATION_USERS)];
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).inviteUser(id),
      setDataFunction: ({ data: updatedUser }) => {
        const updatedUsers = map(organisationUsers, user => {
          if (user.id !== updatedUser.id) return user;
          return updatedUser;
        });
        commit(fromSettings(SET_ORGANISATION_USERS), updatedUsers, {
          root: true
        });
      }
    });
  },
  async [FETCH_USERS]({ commit, dispatch }, params) {
    return handleWithErrorMessage({
      dispatch,
      request: () => api(commit).getUsers(params),
      setLoadingFunction: isLoading => commit(SET_IS_FETCHING_USERS, isLoading),
      setDataFunction: ({ data }) => commit(SET_USERS, data)
    });
  }
};

export default usersActions;
